import {
  AppBox,
  AppDateAndTime,
  AppDatePicker,
  AppGridBox,
  AppSelect,
  AppSkeletonMatchCardList,
  AppText,
  AppTitle,
} from "../../../commons/components";
import { useAppDispatch, useAppSelector } from "../../../hooks/app";
import { useEffect, useState } from "react";
import { ISelectOptions } from "../../../commons/components/ui-components/Select";
import { StatusEnum } from "../../../commons/enums/status-enum";
import { SortOrderEnum } from "../../../commons/enums/sort-order-enum";
import { formatDateTime } from "../../../commons/utilities/date-utils";
import { MatchStatusEnum } from "../../../commons/enums/match-status-enum";
import { fetchManagePlayerCompetitionList } from "../../../redux/slices/football/admin/player/manage-player-competitions-slice";
import ManageMatchCard from "../teams/components/ManageMatchCard";
import {
  cleanUpManagePlayerMatchList,
  fetchManagePlayerMatchList,
  IFootballManagePlayerMatchOption,
} from "../../../redux/slices/football/admin/player/manage-player-matches-slice";
import { FootballMatchDetail } from "../../../commons/models/football/football-match-detail";
import { findIndexByCondition } from "../../../commons/utilities/array-utils";
export default function PlayerManageEvents() {
  const dispatch = useAppDispatch();
  const { playerInfo, playerInfoStatus } = useAppSelector((state) => state.footballManagePlayer);
  const { managePlayerMatchList } = useAppSelector((state) => state.footballManagePlayerMatchList);
  const { playerCompetitionList } = useAppSelector((state) => state.footballManagePlayerCompetitions);
  const [competitionSelectOptions, setCompetitionSelectOptions] = useState<ISelectOptions[]>([]);
  const [seasonSelectOptions, setSeasonSelectOptions] = useState<ISelectOptions[]>([]);
  const [currentCompetitionId, setCurrentCompetitionId] = useState<number>();
  const [currentSeasonId, setCurrentSeasonId] = useState<number>();
  const [currentCompetitionOption, setCurrentCompetitionOption] = useState<ISelectOptions>();
  const [currentSeasonOption, setCurrentSeasonOption] = useState<ISelectOptions>();
  const [liveMatchList, setLiveMatchList] = useState<FootballMatchDetail[]>([]);
  const [upcomingMatchList, setUpcomingMatchList] = useState<FootballMatchDetail[]>([]);
  const [finishedMatchList, setFinishedMatchList] = useState<FootballMatchDetail[]>([]);
  const [matchStartDate, setMatchStartDate] = useState<Date | null>(null);
  const [matchEndDate, setMatchEndDate] = useState<Date | null>(null);

  const onChangeMatchDate = (
    dates: Date | [Date | null, Date | null] | null,
    event: React.SyntheticEvent<any, Event>
  ) => {
    event.preventDefault();
    if (dates instanceof Array) {
      const [start, end] = dates;
      setMatchStartDate(start || null);
      setMatchEndDate(end || null);
    }
  };

  useEffect(() => {
    if (playerInfo.id) {
      dispatch(fetchManagePlayerCompetitionList(playerInfo.id));
    }
  }, [playerInfo]);

  useEffect(() => {
    const competitionOptions: ISelectOptions[] = [];
    if (playerCompetitionList.length > 0) {
      playerCompetitionList.forEach((competition) => {
        competitionOptions.push({
          title: competition.title,
          value: competition.id,
        });
      });
      const currentPlayerCompetitionId = playerInfo.latestSeason?.competition?.id;
      let currentPlayerCompetitionIndex = findIndexByCondition(
        competitionOptions,
        (option) => option.value === currentPlayerCompetitionId
      );
      if (currentPlayerCompetitionIndex >= 0) {
        setCurrentCompetitionOption(competitionOptions[currentPlayerCompetitionIndex]);
        setCurrentCompetitionId(Number(competitionOptions[currentPlayerCompetitionIndex].value));
      } else {
        setCurrentCompetitionId(Number(competitionOptions[0].value));
      }
      setCompetitionSelectOptions(competitionOptions);
    }
  }, [playerCompetitionList]);

  useEffect(() => {
    if (currentCompetitionId) {
      const currentCompetition = playerCompetitionList.first((competition) => competition.id === currentCompetitionId);
      const seasonOptions: ISelectOptions[] = [];
      currentCompetition?.seasons.forEach((season) => {
        seasonOptions.push({ title: season.title, value: season.id });
      });
      const currentPlayerSeasonId = playerInfo.latestSeason?.season?.id;
      let currentPlayerSeasonIndex = findIndexByCondition(
        seasonOptions,
        (option) => option.value === currentPlayerSeasonId
      );
      if (currentPlayerSeasonIndex >= 0) {
        setCurrentSeasonOption(seasonOptions[currentPlayerSeasonIndex]);
        setCurrentSeasonId(Number(seasonOptions[currentPlayerSeasonIndex].value));
      } else {
        setCurrentSeasonOption(seasonOptions[0]);
        setCurrentSeasonId(Number(seasonOptions[0].value));
      }
      setSeasonSelectOptions(seasonOptions);
    }
  }, [currentCompetitionId]);

  useEffect(() => {
    if (currentCompetitionId && currentSeasonId && playerInfo.id) {
      let matchesOption: IFootballManagePlayerMatchOption = {
        playerId: playerInfo.id,
        urlParams: {
          seasonId: currentSeasonId,
          sort: "scheduledAt",
          sortOrder: SortOrderEnum.Descending,
          page: 0,
        },
      };
      if (matchStartDate && matchEndDate) {
        matchesOption = {
          ...matchesOption,
          urlParams: {
            ...matchesOption.urlParams,
            beforeDate: formatDateTime(matchStartDate, "YYYY-MM-DD HH:mm:00"),
            afterDate: formatDateTime(matchEndDate, "YYYY-MM-DD HH:mm:00"),
          },
        };
      } else {
        matchesOption = {
          ...matchesOption,
        };
      }
      dispatch(fetchManagePlayerMatchList(matchesOption));
    } else {
      dispatch(cleanUpManagePlayerMatchList());
    }
  }, [currentCompetitionId, currentSeasonId, playerInfo.additionalInfo, matchEndDate]);

  useEffect(() => {
    const upcomingMatches = managePlayerMatchList.data
      .all()
      .filter((match) => match.status === MatchStatusEnum.Upcoming);
    const liveMatches = managePlayerMatchList.data.all().filter((match) => match.status === MatchStatusEnum.Live);
    const finishedMatches = managePlayerMatchList.data
      .all()
      .filter((match) => match.status === MatchStatusEnum.Finished);
    setLiveMatchList(liveMatches);
    setUpcomingMatchList(upcomingMatches);
    setFinishedMatchList(finishedMatches);
  }, [managePlayerMatchList]);

  useEffect(() => {
    if (currentSeasonId) {
      let intervalId: NodeJS.Timeout;
      intervalId = setInterval(() => {
        let matchesOption: IFootballManagePlayerMatchOption = {
          playerId: playerInfo.id,
          urlParams: {
            seasonId: currentSeasonId,
            sort: "scheduledAt",
            sortOrder: SortOrderEnum.Descending,
            page: 0,
          },
        };
        if (matchStartDate && matchEndDate) {
          matchesOption = {
            ...matchesOption,
            urlParams: {
              ...matchesOption.urlParams,
              beforeDate: formatDateTime(matchStartDate, "YYYY-MM-DD HH:mm:00"),
              afterDate: formatDateTime(matchEndDate, "YYYY-MM-DD HH:mm:00"),
            },
          };
        } else {
          matchesOption = {
            ...matchesOption,
          };
        }
        dispatch(fetchManagePlayerMatchList(matchesOption));
      }, 30000);
      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }
  }, [managePlayerMatchList]);

  return (
    <AppBox mb="sm" displayBlock={true}>
      <AppBox flexDirection="column" gap="md">
        <AppBox flexDirection="column" gap="sm">
          {playerInfoStatus === StatusEnum.Succeeded && (
            <AppBox flexDirection="column" gap="xs">
              <AppBox justifyContent="space-between">
                <AppTitle as="h5">Matches & Result</AppTitle>
                <AppBox>
                  <AppDatePicker
                    onChange={onChangeMatchDate}
                    selectedStart={matchStartDate}
                    selectedEnd={matchEndDate}
                    isMultiSelector
                    isIconButton
                    isClearable
                    calenderPlacement="left-start"
                  />
                </AppBox>
              </AppBox>
              <AppGridBox columns={2} gap="sm">
                {competitionSelectOptions.length > 0 && (
                  <AppSelect
                    options={competitionSelectOptions}
                    currentOption={currentCompetitionOption}
                    onChange={(option) => {
                      setCurrentCompetitionId(Number(option.value));
                    }}
                  />
                )}
                {seasonSelectOptions.length > 0 && (
                  <AppSelect
                    currentOption={currentSeasonOption}
                    options={seasonSelectOptions}
                    onChange={(option) => {
                      setCurrentSeasonId(Number(option.value));
                    }}
                  />
                )}
              </AppGridBox>
            </AppBox>
          )}
          {managePlayerMatchList.status === StatusEnum.Loading ? (
            <AppSkeletonMatchCardList />
          ) : (
            <>
              {managePlayerMatchList.data.length === 0 ? (
                <AppText>There are not any matches for this player.</AppText>
              ) : (
                <>
                  {liveMatchList.length > 0 && (
                    <AppBox flexDirection="column" gap="xs">
                      <AppTitle as="h6">Live Matches</AppTitle>
                      <AppBox flexDirection="column" gap="sm">
                        {liveMatchList.map((match, index) => (
                          <AppBox flexDirection="column" gap="2xs" key={index}>
                            <AppDateAndTime date={match.scheduledAt} />
                            <AppBox flexDirection="column" gap="sm">
                              <ManageMatchCard key={match.id} match={match} />
                            </AppBox>
                          </AppBox>
                        ))}
                      </AppBox>
                    </AppBox>
                  )}
                  {upcomingMatchList.length > 0 && (
                    <AppBox flexDirection="column" gap="xs">
                      <AppTitle as="h6">Upcoming Matches</AppTitle>
                      <AppBox flexDirection="column" gap="sm">
                        {upcomingMatchList.map((match, index) => (
                          <AppBox flexDirection="column" gap="2xs" key={index}>
                            <AppDateAndTime date={match.scheduledAt} />
                            <AppBox flexDirection="column" gap="sm">
                              <ManageMatchCard match={match} />
                            </AppBox>
                          </AppBox>
                        ))}
                      </AppBox>
                    </AppBox>
                  )}
                  {finishedMatchList.length > 0 && (
                    <AppBox flexDirection="column" gap="xs">
                      <AppTitle as="h6">Previous Matches</AppTitle>
                      <AppBox flexDirection="column" gap="sm">
                        {finishedMatchList.map((match, index) => (
                          <AppBox flexDirection="column" gap="2xs" key={index}>
                            <AppDateAndTime date={match.scheduledAt} />
                            <AppBox flexDirection="column" gap="sm">
                              <ManageMatchCard match={match} />
                            </AppBox>
                          </AppBox>
                        ))}
                      </AppBox>
                    </AppBox>
                  )}
                </>
              )}
            </>
          )}
        </AppBox>
      </AppBox>
    </AppBox>
  );
}
